import React, { Suspense, lazy } from "react";
import {
  Route,
  createBrowserRouter,
  createRoutesFromElements,
  defer,
} from "react-router-dom";
import "./App.css";
import {
  ROUTE,
  CATEGORY_ROUTER,
  CONTACT_US_ROUTER,
  ERROR_ROUTER,
  LOGIN_ROUTER,
  PROJECT_ROUTER,
  SETTINGS_ROUTER,
  DASHBOARD_ROUTER,
  JOB_APPLY_ROUTER,
  TESTIMONIAL_ROUTER,
  ENQUIRE_ROUTER,
  CAREER_CATEGORY_ROUTER,
} from "@app/constant/routerConstant";
import LoadingScreen from "@app/components/loading-screen";
import { getAuthUser } from "@app/utils/auth";

// ----------------------------------------------------------------------

// Layouts
const DashboardLayout = lazy(() => import("@app/layout/Layout/Layout"));
const AuthProviderLayout = lazy(
  () => import("@app/layout/RoutesLayout/AuthProviderLayout"),
);
const ProtectedRoute = lazy(
  () => import("@app/layout/RoutesLayout/ProtectedRoute"),
);
const PublicRoute = lazy(() => import("@app/layout/RoutesLayout/PublicRoute"));

// Pages
const LoginPage = lazy(() => import("./pages/login"));
const DashboardPage = lazy(() => import("./pages/dashboard"));
const CategoryListPage = lazy(() => import("./pages/category/category-list"));
const CategoryPage = lazy(() => import("./pages/category/category-page"));
const ProjectListPage = lazy(() => import("./pages/project/project-list"));
const ProjectPage = lazy(() => import("./pages/project/project-page"));
const TestimonialListPage = lazy(
  () => import("./pages/testimonial/testimonnial-list"),
);
const TestimonialPage = lazy(
  () => import("./pages/testimonial/testimonial-page"),
);
const CareerCategoryListPage = lazy(
  () => import("./pages/career-category/career-category-list"),
);
const CareerCategoryPage = lazy(
  () => import("./pages/career-category/career-category-page"),
);
const ContactUsListPage = lazy(
  () => import("./pages/contact-us/contact-us-list"),
);
const ContactUsDetailPage = lazy(
  () => import("./pages/contact-us/contact-us-detail"),
);
const EnquireListView = lazy(
  () => import("./section/Enquire/enquire-list-view"),
);
const EnquireDetailPage = lazy(() => import("./pages/enquire/enquire-detail"));
const SettingListPage = lazy(() => import("./pages/setting/setting"));
const BlogListPage = lazy(() => import("./pages/blog/blog-list"));
const BlogPage = lazy(() => import("./pages/blog/blog-page"));
const CompliancePage = lazy(() => import("./pages/compliance/compliance-page"));
const JobApplyPage = lazy(() => import("./pages/job-apply/job-apply-list"));
const ChannelPartnerPage = lazy(
  () => import("./pages/channel-partner/channel-partner-list"),
);
const AboutUsDetailsListPage = lazy(
  () => import("./pages/about-us/about-us-details-list"),
);
const AboutUsDetailsPage = lazy(
  () => import("./pages/about-us/about-us-details-page"),
);
const AboutUsTeamListPage = lazy(
  () => import("./pages/about-us/about-us-team-list"),
);
const AboutUsTeamPage = lazy(
  () => import("./pages/about-us/about-us-team-page"),
);
const AboutUsShowcaseListPage = lazy(
  () => import("./pages/about-us/about-us-showcase-list"),
);
const AboutUsShowcasePage = lazy(
  () => import("./pages/about-us/about-us-showcase-page"),
);
const AboutUsOtherFieldsPage = lazy(
  () => import("src/pages/about-us/about-us-other-fields-page"),
);
const NoMatch404 = lazy(() => import("./pages/NoMatch404"));

const getUserData = () =>
  new Promise((resolve) => {
    const user = getAuthUser();
    resolve(user);
  });

const AppRoutes = createBrowserRouter(
  createRoutesFromElements(
    <Route
      element={
        <Suspense fallback={<LoadingScreen />}>
          <AuthProviderLayout />
        </Suspense>
      } // auth provider
      loader={() => defer({ userPromise: getUserData() })}
    >
      {/* ↓↓↓↓ PUBLIC ROUTES in ↓↓↓↓ */}
      <Route path={LOGIN_ROUTER} element={<LoginPage />} />

      <Route
        element={<PublicRoute />}
        loader={() => defer({ userPromise: getUserData() })}
      >
        <Route path="/" element={<LoginPage />} />
      </Route>

      {/* ↓↓↓↓ all login protected route ↓↓↓↓ */}
      <Route
        element={<ProtectedRoute />}
        loader={() => defer({ userPromise: getUserData() })}
      >
        <Route element={<DashboardLayout />}>
          <Route path={DASHBOARD_ROUTER} element={<DashboardPage />} />

          <Route path={CATEGORY_ROUTER} element={<CategoryListPage />} />
          <Route path={`${CATEGORY_ROUTER}/:id`} element={<CategoryPage />} />

          <Route path={PROJECT_ROUTER} element={<ProjectListPage />} />
          <Route path={`${PROJECT_ROUTER}/:id`} element={<ProjectPage />} />

          <Route path={TESTIMONIAL_ROUTER} element={<TestimonialListPage />} />
          <Route
            path={`${TESTIMONIAL_ROUTER}/:id`}
            element={<TestimonialPage />}
          />

          <Route
            path={CAREER_CATEGORY_ROUTER}
            element={<CareerCategoryListPage />}
          />
          <Route
            path={`${CAREER_CATEGORY_ROUTER}/:id`}
            element={<CareerCategoryPage />}
          />

          <Route path={CONTACT_US_ROUTER} element={<ContactUsListPage />} />
          <Route
            path={`${CONTACT_US_ROUTER}/:id`}
            element={<ContactUsDetailPage />}
          />

          <Route path={ENQUIRE_ROUTER} element={<EnquireListView />} />
          <Route
            path={`${ENQUIRE_ROUTER}/:id`}
            element={<EnquireDetailPage />}
          />
          <Route path={`${SETTINGS_ROUTER}`} element={<SettingListPage />} />

          <Route path={ROUTE.BLOG.LIST} element={<BlogListPage />} />
          <Route path={`${ROUTE.BLOG.LIST}/:id`} element={<BlogPage />} />

          {/* One route so switching projects keeps the page mounted. */}
          <Route
            path={`${ROUTE.COMPLIANCE.LIST}/:id?`}
            element={<CompliancePage />}
          />

          <Route path={JOB_APPLY_ROUTER} element={<JobApplyPage />} />

          <Route
            path={ROUTE.CHANNEL_PARTNER.INDIVIDUAL}
            element={<ChannelPartnerPage />}
          />

          <Route
            path={ROUTE.CHANNEL_PARTNER.AGENCY}
            element={<ChannelPartnerPage />}
          />

          <Route
            path={ROUTE.ABOUT_US.LIST}
            element={<AboutUsDetailsListPage />}
          />
          <Route
            path={`${ROUTE.ABOUT_US.LIST}/edit`}
            element={<AboutUsDetailsPage />}
          />
          <Route
            path={`${ROUTE.ABOUT_US.LIST}/edit/:section`}
            element={<AboutUsDetailsPage />}
          />
          <Route
            path={ROUTE.ABOUT_US.TEAMS}
            element={<AboutUsTeamListPage />}
          />
          <Route
            path={`${ROUTE.ABOUT_US.TEAMS}/:id`}
            element={<AboutUsTeamPage />}
          />

          <Route
            path={ROUTE.ABOUT_US.SHOWCASE}
            element={<AboutUsShowcaseListPage />}
          />
          <Route
            path={`${ROUTE.ABOUT_US.SHOWCASE}/:id`}
            element={<AboutUsShowcasePage />}
          />
          <Route
            path={ROUTE.ABOUT_US.OTHER_FIELDS}
            element={<AboutUsOtherFieldsPage />}
          />
        </Route>
      </Route>

      {/* Catch-all 404 Page */}
      <Route path={ERROR_ROUTER} element={<NoMatch404 />} />
    </Route>,
  ),
);

export default AppRoutes;
